import React, { useEffect, useState } from 'react';
import {
  ChakraProvider,
  CircularProgress,
  Flex,
  theme,
} from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useTranslation } from 'react-i18next';
import Header from './components/Header/Header';
import { UserProvider } from './context/UserContext';
import Main from './components/Main/Main';
import { UserLocationProvider } from './context/UserLocation';
import Error from './components/Error/Error';
import ReactAdmin from './admin/Admin';
import PlaygroundDetail from './components/PlaygroundDetail/PlaygroundDetail';
import { SearchProvider } from './context/SearchContext';
import Profile from './components/Profile/Profile';
import Playgrounds from './components/PlaygroundList/Playgrounds';

const AppHeader = () => {
  return <Header />;
};

const App = () => {
  const queryClient = new QueryClient();
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(true);

  const router = createBrowserRouter([
    {
      path: '/',
      element: (
        <UserLocationProvider>
          <AppHeader />
          <Main />
        </UserLocationProvider>
      ),
      errorElement: <Error />,
    },
    {
      path: '/profile',
      element: (
        <UserLocationProvider>
          <AppHeader />
          <Profile />
        </UserLocationProvider>
      ),
    },
    {
      path: '/profile/playgrounds/:playgroundId',
      element: (
        <UserLocationProvider>
          <AppHeader />
          <PlaygroundDetail />
        </UserLocationProvider>
      ),
    },
    {
      path: '/playgrounds',
      element: (
        <UserLocationProvider>
          <AppHeader />
          <Playgrounds />
        </UserLocationProvider>
      ),
      errorElement: <Error />,
    },
    {
      path: '/playgrounds/:playgroundId',
      element: (
        <UserLocationProvider>
          <AppHeader />
          <PlaygroundDetail />
        </UserLocationProvider>
      ),
      errorElement: <Error />,
    },
    {
      path: '/admin/*',
      element: <ReactAdmin />,
    },
  ]);

  useEffect(() => {
    if (i18n.isInitialized) {
      setLoading(false);
    } else {
      i18n.on('initialized', () => {
        setLoading(false);
      });
      return () => {
        i18n.off('initialized');
      };
    }
    return undefined;
  }, [i18n]);

  if (loading) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        height="100vh"
        position="fixed"
        top="0"
        left="0"
        width="100%"
        background="rgba(255, 255, 255, 0.7)"
        zIndex="999"
      >
        <CircularProgress
          color="teal.500"
          size={12}
          thickness={10}
          isIndeterminate
        />
      </Flex>
    );
  }

  return (
    <ChakraProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <UserProvider>
          <SearchProvider>
            <RouterProvider router={router} />
          </SearchProvider>
        </UserProvider>
        <ReactQueryDevtools />
      </QueryClientProvider>
    </ChakraProvider>
  );
};

export default App;
